import React, { useState, useEffect } from 'react';
import { versionLabel } from './utils/appVersion';
import './App.css';
import Header from './components/Header';
import ClockControls from './components/ClockControls';
import TimeEntriesTable from './components/TimeEntriesTable';
import ManualEntryModal from './components/ManualEntryModal';
import UserSetupModal from './components/UserSetupModal';
import WeeklyTimesheet from './components/WeeklyTimesheet';
import VoiceCommandsModal from './components/VoiceCommandsModal';
import Settings from './components/Settings';
import ListManager from './components/ListManager';
import EnrollmentModal from './components/EnrollmentModal';
import ReceiptCapture from './components/ReceiptCapture';
import PendingReceipts from './components/PendingReceipts';
import { db } from './services/db';
import { isEnrolled } from './services/enrollmentService';
import { autoSync, getPendingCount, syncProjectsFromDashboard } from './services/syncService';
import { syncPendingReceipts, getPendingReceiptCount } from './services/receiptQueue';
import { processUrlParams, getPendingVoiceCommands } from './utils/urlUtils';
import { initAnalytics, trackEvent } from './utils/analytics';
import { exportAllToCsv } from './utils/exportUtils';

function App() {
  const [isManualEntryModalOpen, setIsManualEntryModalOpen] = useState(false);
  const [isUserSetupModalOpen, setIsUserSetupModalOpen] = useState(false);
  const [isEnrollmentModalOpen, setIsEnrollmentModalOpen] = useState(false);
  const [enrollmentDismissed, setEnrollmentDismissed] = useState(false);
  const [enrolledIdentity, setEnrolledIdentity] = useState<{ name?: string; email?: string | null } | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [isReceiptCaptureOpen, setIsReceiptCaptureOpen] = useState(false);
  const [isPendingReceiptsOpen, setIsPendingReceiptsOpen] = useState(false);
  const [pendingReceiptCount, setPendingReceiptCount] = useState(0);
  const [isWeeklyTimesheetOpen, setIsWeeklyTimesheetOpen] = useState(false);
  const [isVoiceCommandsModalOpen, setIsVoiceCommandsModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [hasPendingVoiceCommands, setHasPendingVoiceCommands] = useState(false);
  // We need setUserName but don't directly use userName in this component
  const [, setUserName] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize analytics
        initAnalytics();

        // Process any incoming Siri or shortcut URL parameters immediately.
        // This keeps voice commands working even when the user has not completed setup yet.
        await processUrlParams();
        
        // Check if the user has set up their name
        const name = await db.getUserName();
        setUserName(name);

        // Check for pending voice commands
        await checkPendingVoiceCommands();

        // Open the voice commands modal directly when requested.
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('openVoiceCommands')) {
          urlParams.delete('openVoiceCommands');
          const remainingQuery = urlParams.toString();
          window.history.replaceState(
            {},
            document.title,
            `${window.location.pathname}${remainingQuery ? `?${remainingQuery}` : ''}`
          );

          const pendingCommands = await getPendingVoiceCommands();
          if (pendingCommands.length > 0) {
            setIsVoiceCommandsModalOpen(true);
          }
        }

        if (!name) {
          setIsUserSetupModalOpen(true);
        }
      } catch (error) {
        console.error('Error initializing app:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Check for pending voice commands periodically
  useEffect(() => {
    const intervalId = setInterval(checkPendingVoiceCommands, 30000); // Check every 30 seconds
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Runs once when the app opens: prompt for setup if this device has never
  // been enrolled, then push anything outstanding without waiting for anyone
  // to press Sync. Storage on a device that isn't installed to the Home
  // Screen can be evicted after about a week of disuse, and manual-only sync
  // left a week of work exposed.
  useEffect(() => {
    const onOpen = async () => {
      try {
        if (!(await isEnrolled())) {
          setIsEnrollmentModalOpen(true);
        }
        // autoSync() never rejects (it catches internally), but the reads
        // around it can — a DB/storage failure here must not become an
        // unhandled rejection in an effect that has no caller to catch it.
        await autoSync();
        setPendingCount(await getPendingCount());
        // Refresh the office's job list. Last, so the pending count — the
        // number an employee actually looks at to know their hours got out —
        // never waits on it. syncProjectsFromDashboard swallows its own
        // errors, so a dead network cannot break this effect.
        await syncProjectsFromDashboard();
        // Drain queued receipts last, for the same reason project sync is last:
        // the pending-entries count is what an employee checks to know their
        // hours got out, and it must not wait on an image upload.
        await syncPendingReceipts();
        setPendingReceiptCount(await getPendingReceiptCount());
      } catch (error) {
        console.error('Error checking enrollment/sync state on open:', error);
      }
    };
    onOpen();
  }, []);

  const checkPendingVoiceCommands = async () => {
    try {
      const pendingCommands = await getPendingVoiceCommands();
      const hasPending = pendingCommands.length > 0;
      setHasPendingVoiceCommands(hasPending);
      
      // Request notification permission if there are pending commands
      if (hasPending && 'Notification' in window && Notification.permission === 'default') {
        await Notification.requestPermission();
      }
    } catch (error) {
      console.error('Error checking pending voice commands:', error);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  }

  const handleBackup = async () => {
    await exportAllToCsv();
  };

  // Export functionality moved to WeeklyTimesheet component

  const handleUserSetupComplete = () => {
    setIsUserSetupModalOpen(false);
    handleRefresh();
    checkPendingVoiceCommands();
  };

  if (isLoading) {
    return <div className="app-loading">Loading...</div>;
  }

  return (
    <div className="app-container">
      <Header />
      
      <div className="main-content">
        <div className="manual-entry-container">
          <button 
            className="action-button manual-entry-button"
            onClick={() => {
              setIsManualEntryModalOpen(true);
              trackEvent('open_manual_entry');
            }}
          >
            Manual Entry
          </button>
          <button
            className="action-button receipt-button"
            onClick={() => {
              setIsReceiptCaptureOpen(true);
              trackEvent('open_receipt_capture');
            }}
          >
            New Receipt
          </button>
          {pendingReceiptCount > 0 && (
            <button
              className="action-button receipt-pending-button"
              onClick={() => setIsPendingReceiptsOpen(true)}
            >
              Pending ({pendingReceiptCount})
            </button>
          )}
        </div>
        
        <ClockControls onStatusChange={handleRefresh} />
        
        <TimeEntriesTable refreshTrigger={refreshTrigger} />
        
        <ListManager />
        
        <div className="action-buttons">
          <button 
            className="action-button weekly-button"
            onClick={() => {
              setIsWeeklyTimesheetOpen(true);
              trackEvent('open_weekly_timesheet');
            }}
          >
            Weekly Timesheet
          </button>
          
          <button 
            className="action-button settings-button"
            onClick={() => {
              setIsSettingsOpen(true);
              trackEvent('open_settings');
            }}
          >
            Settings
          </button>
          
          <button 
            className="action-button backup-button"
            onClick={handleBackup}
          >
            Backup Entries
          </button>
          
          {hasPendingVoiceCommands && (
            <button 
              className="action-button voice-button"
              onClick={() => {
                setIsVoiceCommandsModalOpen(true);
                trackEvent('open_voice_commands');
              }}
            >
              Voice Commands
              <span className="notification-badge"></span>
            </button>
          )}
        </div>
      </div>
      
      {isManualEntryModalOpen && (
        <ManualEntryModal
          onClose={() => setIsManualEntryModalOpen(false)}
          onSave={handleRefresh}
          refreshTrigger={refreshTrigger}
        />
      )}
      
      {isUserSetupModalOpen && !isEnrollmentModalOpen && (
        <UserSetupModal
          onComplete={handleUserSetupComplete}
          initialName={enrolledIdentity?.name}
          initialEmail={enrolledIdentity?.email}
        />
      )}

      {isEnrollmentModalOpen && (
        <EnrollmentModal
          onComplete={async (identity) => {
            setIsEnrollmentModalOpen(false);
            setEnrollmentDismissed(false);
            setEnrolledIdentity(identity);
            // The code told us who this phone belongs to. Offer setup
            // pre-filled so the employee confirms rather than retypes — but
            // only if they haven't already set themselves up.
            if (!(await db.getUserName())) setIsUserSetupModalOpen(true);
          }}
          onDismiss={() => { setIsEnrollmentModalOpen(false); setEnrollmentDismissed(true); }}
        />
      )}

      {enrollmentDismissed && (
        <div
          onClick={() => setIsEnrollmentModalOpen(true)}
          style={{
            background: '#fef3c7', color: '#92400e', padding: '0.5rem 1rem',
            textAlign: 'center', cursor: 'pointer', fontSize: '0.875rem',
          }}
        >
          This device isn&apos;t set up yet — tap to enter your code.
        </div>
      )}

      {pendingCount > 0 && (
        <div style={{ background: '#e0f2fe', color: '#075985', padding: '0.25rem 1rem', textAlign: 'center', fontSize: '0.8125rem' }}>
          {pendingCount} {pendingCount === 1 ? 'entry' : 'entries'} not yet sent to the office
        </div>
      )}

      {isWeeklyTimesheetOpen && (
        <WeeklyTimesheet onClose={() => setIsWeeklyTimesheetOpen(false)} />
      )}
      
      {isVoiceCommandsModalOpen && (
        <VoiceCommandsModal 
          onClose={() => setIsVoiceCommandsModalOpen(false)}
          onSave={() => {
            handleRefresh();
            checkPendingVoiceCommands();
          }}
        />
      )}
      
      {isSettingsOpen && (
        <Settings
          onClose={() => setIsSettingsOpen(false)}
          onSave={() => {
            handleRefresh();
            // setIsSettingsOpen(false); // Optional: keep open or close on save
          }}
        />
      )}

      {isReceiptCaptureOpen && (
        <ReceiptCapture
          onClose={() => setIsReceiptCaptureOpen(false)}
          onQueued={async () => setPendingReceiptCount(await getPendingReceiptCount())}
        />
      )}

      {isPendingReceiptsOpen && (
        <PendingReceipts
          onClose={async () => {
            setIsPendingReceiptsOpen(false);
            setPendingReceiptCount(await getPendingReceiptCount());
          }}
        />
      )}

      {/*
        Both halves are worked out at build time — see src/utils/appVersion.ts.
        The date answers "is this phone current?" at a glance; the build id
        answers "exactly which build is it?" when someone reports a problem.
        Neither needs anyone to remember to bump a number.
      */}
      <div className="app-version">
        Time Trakr Lite {versionLabel()}
      </div>
    </div>
  );
}

export default App;
